'use client';

import { PlayerLeaderboardRank } from '@wwe-portal/entities/leaderboard.type';
import { getPlayerLeaderboardTop } from '@wwe-portal/web/services/client/leaderboard';
import { FC, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { LeaderboardHead } from './leaderboard-head';
import {
  PlayerLeaderboard,
  PlayerLeaderboardSkeleton,
  PlayerRowHeaderDesktop,
} from './player-leaderboard';
import { MAX_PLAYER_LEADERBOARD_LIMIT } from '@wwe-portal/web/utils/constants/leaderboard';
import { PaginationBar } from '../../../../../packages/ui/components/pagination/pagination-bar';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { QUERY_KEYS_LEADERBOARD_PLAYER_TOP } from '@wwe-portal/web/utils/constants/query-key';
import { LeaderboardLoadMoreButton } from './leaderboard-loadmore-button';

interface TopPlayerTableProps {
  perPage: number;
  name: string;
  category: string;
  pointColumn: string;
}

const TableHeader: FC<{ name: string }> = ({ name }) => (
  <LeaderboardHead
    hideLiveTag
    name={name}
    moreHref=""
    moreText="See Top 1000"
  />
);

const Table: FC<{
  data: PlayerLeaderboardRank[];
  name: string;
  pointColumn: string;
}> = ({ data, name, pointColumn }) => {
  const columnNames = { point: pointColumn };
  return (
    <PlayerLeaderboard
      compact={false}
      data={data.map((player) => ({
        points: player.playerPoints,
        rank: player.playerRank,
        playerId: player.userId,
        player: player.playerName,
        name: player.playerName,
        faction: player.factionName,
        userId: player.userId,
      }))}
      mobileHeader={<TableHeader name={name} />}
      desktopHeader={<TableHeader name={name} />}
      customColumnNames={columnNames}
      desktopRowHeader={
        <PlayerRowHeaderDesktop
          customColumnName={columnNames}
          compact={false}
        />
      }
    />
  );
};

export const InfiniteTopPlayerTable: FC<TopPlayerTableProps> = ({
  perPage,
  name,
  category,
  pointColumn,
}) => {
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [QUERY_KEYS_LEADERBOARD_PLAYER_TOP, { category, perPage }],
      queryFn: async ({ pageParam }) => {
        const response = await getPlayerLeaderboardTop({
          category,
          skip: pageParam * perPage,
          limit: perPage,
        });
        return response.data.data.leaderboard;
      },
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length < perPage) {
          return undefined;
        }
        if (pages.length * perPage >= MAX_PLAYER_LEADERBOARD_LIMIT) {
          return undefined;
        }
        return pages.length;
      },
      initialPageParam: 0,
    });

  const allData = data?.pages.flatMap((page) => page);
  return (
    <div>
      {!allData && (
        <PlayerLeaderboardSkeleton
          compact={false}
          mobileHeader={<TableHeader name={name} />}
          desktopHeader={<TableHeader name={name} />}
          nRows={perPage}
          desktopRowHeader={
            <PlayerRowHeaderDesktop
              compact={false}
              customColumnName={{ point: pointColumn }}
            />
          }
        />
      )}
      {allData && (
        <Table data={allData} name={name} pointColumn={pointColumn} />
      )}
      {hasNextPage && (
        <LeaderboardLoadMoreButton
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          className="mt-8"
        />
      )}
    </div>
  );
};

export const PaginatedTopPlayerTable: FC<TopPlayerTableProps> = ({
  perPage,
  name,
  category,
  pointColumn,
}) => {
  const [page, setPage] = useState(0);
  const { data } = useQuery({
    queryKey: [
      QUERY_KEYS_LEADERBOARD_PLAYER_TOP,
      {
        category,
        page,
        perPage,
      },
    ],
    queryFn: async () => {
      const response = await getPlayerLeaderboardTop({
        category: Number(category),
        skip: page * perPage,
        limit: perPage,
      });
      return {
        leaderboard: response.data.data.leaderboard,
        totalPages: Math.ceil(Number(response.data.data.totalCount) / perPage),
      };
    },
  });

  return (
    <div>
      {!data && (
        <PlayerLeaderboardSkeleton
          compact={false}
          mobileHeader={<TableHeader name={name} />}
          desktopHeader={<TableHeader name={name} />}
          nRows={perPage}
          desktopRowHeader={
            <PlayerRowHeaderDesktop
              compact={false}
              customColumnName={{ point: pointColumn }}
            />
          }
        />
      )}
      {data && (
        <Table data={data.leaderboard} name={name} pointColumn={pointColumn} />
      )}
      {data?.totalPages && (
        <div className="mt-8 w-full">
          <PaginationBar
            page={page}
            totalPages={Number(data.totalPages)}
            showPages={5}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );
};

export const TopPlayerTable: FC<TopPlayerTableProps> = ({
  perPage,
  name,
  category,
  pointColumn,
}) => {
  const { isMobile, isHydrated } = useResponsive();

  if (!isHydrated) {
    return (
      <PlayerLeaderboardSkeleton
        compact={false}
        mobileHeader={<TableHeader name={name} />}
        desktopHeader={<TableHeader name={name} />}
        nRows={perPage}
        desktopRowHeader={
          <PlayerRowHeaderDesktop
            compact={false}
            customColumnName={{ point: pointColumn }}
          />
        }
      />
    );
  }

  if (isMobile) {
    return (
      <InfiniteTopPlayerTable
        perPage={perPage}
        name={name}
        category={category}
        pointColumn={pointColumn}
      />
    );
  }
  return (
    <PaginatedTopPlayerTable
      perPage={perPage}
      name={name}
      category={category}
      pointColumn={pointColumn}
    />
  );
};
